import React from "react";
import {
  Box,
  Button,
  Chip,
  Menu,
  MenuItem,
  Stack,
  Typography,
} from "@mui/material";
import { FilterListRounded, StarRounded } from "@mui/icons-material";
import DatesItem from "./DatesItem";
import {
  EmptyState,
  PageIntro,
  PageShell,
  SectionBlock,
  StatStrip,
} from "../ui/AppPrimitives";
import { getRelativeDayLabel, startOfDay } from "../utils/dateUtils";

const filterSections = [
  {
    title: "View",
    options: [
      { value: "all", label: "All Priority Dates" },
      { value: "today", label: "Today" },
      { value: "week", label: "This Week" },
      { value: "month", label: "This Month" },
    ],
  },
  {
    title: "Category",
    options: [
      { value: "personal", label: "Personal" },
      { value: "legal", label: "Legal" },
      { value: "health", label: "Health" },
      { value: "exam", label: "Exam" },
      { value: "other", label: "Other" },
    ],
  },
];

const ImportantDates = ({ dates, onDelete, onPriority }) => {
  const [filter, setFilter] = React.useState("all");
  const [anchorEl, setAnchorEl] = React.useState(null);
  const isOpen = Boolean(anchorEl);

  const grouped = React.useMemo(() => {
    const today = startOfDay(new Date());
    const endOfWeek = new Date(today);
    endOfWeek.setDate(today.getDate() + (7 - today.getDay()));
    const endOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    const endOfYear = new Date(today.getFullYear(), 11, 31);

    const result = {
      today: [],
      week: [],
      month: [],
      year: [],
      later: [],
    };

    const filtered = dates
      .filter((item) => {
        if (!item.priority || !item.dateTime) return false;
        const eventDate = startOfDay(item.dateTime);
        if (eventDate < today) return false;

        switch (filter) {
          case "today":
            return eventDate.getTime() === today.getTime();
          case "week":
            return eventDate > today && eventDate <= endOfWeek;
          case "month":
            return eventDate > today && eventDate <= endOfMonth;
          case "personal":
          case "legal":
          case "health":
          case "exam":
          case "other":
            return item.category?.toLowerCase() === filter;
          default:
            return true;
        }
      })
      .sort((a, b) => new Date(a.dateTime) - new Date(b.dateTime));

    filtered.forEach((item) => {
      const itemDate = startOfDay(item.dateTime);
      if (itemDate.getTime() === today.getTime()) {
        result.today.push(item);
      } else if (itemDate <= endOfWeek) {
        result.week.push(item);
      } else if (itemDate <= endOfMonth) {
        result.month.push(item);
      } else if (itemDate <= endOfYear) {
        result.year.push(item);
      } else {
        result.later.push(item);
      }
    });

    return result;
  }, [dates, filter]);

  const sortedImportantDates = Object.values(grouped).flat();
  const nextUp = sortedImportantDates[0];
  const filterLabel =
    filterSections
      .flatMap((section) => section.options)
      .find((option) => option.value === filter)?.label || "All Priority Dates";

  const renderGroup = (label, items) => {
    if (items.length === 0) return null;

    return (
      <SectionBlock
        key={label}
        title={label}
        subtitle={`${items.length} event${items.length > 1 ? "s" : ""}`}
      >
        <Stack spacing={1.6}>
          {items.map((item) => (
            <DatesItem
              key={item.id}
              dateItem={item}
              onDelete={onDelete}
              onPriority={onPriority}
            />
          ))}
        </Stack>
      </SectionBlock>
    );
  };

  return (
    <PageShell maxWidth="xl">
      <PageIntro
        eyebrow="Priority"
        title="Your most important dates, separated from the noise."
        subtitle="Use this view to focus on deadlines and moments that need extra attention."
        action={
          <Button
            variant="outlined"
            color="inherit"
            startIcon={<FilterListRounded />}
            onClick={(event) => setAnchorEl(event.currentTarget)}
          >
            {filterLabel}
          </Button>
        }
      />

      <StatStrip
        items={[
          {
            label: "Priority events",
            value: sortedImportantDates.length,
            caption: "Upcoming dates marked important",
            tint: "#f59e0b",
          },
          {
            label: "Next up",
            value: nextUp ? getRelativeDayLabel(nextUp.dateTime) : "—",
            caption: nextUp ? nextUp.title : "No priority event queued",
            tint: "#2563eb",
          },
        ]}
      />

      {sortedImportantDates.length === 0 ? (
        <EmptyState
          icon={<StarRounded sx={{ fontSize: 40, color: "#f59e0b" }} />}
          title="No upcoming priority dates"
          description="Mark a future event as important and it will appear here for faster focus."
        />
      ) : (
        <Box>
          {renderGroup("Today", grouped.today)}
          {renderGroup("This Week", grouped.week)}
          {renderGroup("This Month", grouped.month)}
          {renderGroup("This Year", grouped.year)}
          {renderGroup("Later", grouped.later)}
        </Box>
      )}

      <Menu
        anchorEl={anchorEl}
        open={isOpen}
        onClose={() => setAnchorEl(null)}
        PaperProps={{
          sx: {
            width: 260,
            borderRadius: 4,
            mt: 1,
          },
        }}
      >
        {filterSections.map((section) => (
          <Box key={section.title} sx={{ py: 0.4 }}>
            <Typography
              variant="overline"
              sx={{ px: 2, color: "text.secondary", display: "block" }}
            >
              {section.title}
            </Typography>
            {section.options.map((option) => (
              <MenuItem
                key={option.value}
                selected={filter === option.value}
                onClick={() => {
                  setFilter(option.value);
                  setAnchorEl(null);
                }}
              >
                <Stack direction="row" justifyContent="space-between" sx={{ width: "100%" }}>
                  <span>{option.label}</span>
                  {filter === option.value && (
                    <Chip label="Active" size="small" color="primary" />
                  )}
                </Stack>
              </MenuItem>
            ))}
          </Box>
        ))}
      </Menu>
    </PageShell>
  );
};

export default ImportantDates;
